/**
 * FauxDS Radio - Recreated from Figma MCP
 *
 * ⚠️ This is a SIMPLIFIED RECREATION of the Slate Radio component for prototyping.
 * It is NOT the real @faire/slate Radio and should NOT be used for:
 * - Engineering handoff
 * - Design specs
 * - Production code
 *
 * For the real Slate Radio, see: @faire/slate/components/radio
 *
 * This component was recreated from Figma MCP design specs:
 * - Figma node: 3572:20 (Slate Radio)
 * - Design tokens extracted from Figma variables
 * - All variants verified: Checked (true/false), Label (true/false), Disabled (true/false)
 * - Focused state verified
 */
import { cn } from "@faire/prototype-playground/lib/utils";
import React, { forwardRef, useId } from "react";

export interface RadioProps {
  /**
   * The label text to display
   * @default "Label"
   */
  label?: string;
  /**
   * Whether the radio is checked
   * @default false
   */
  checked?: boolean;
  /**
   * Whether the radio is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Change handler
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Radio name attribute (for grouping)
   */
  name?: string;
  /**
   * Radio value attribute
   */
  value?: string;
  /**
   * **playground only**
   * Optional className for additional styling
   */
  className?: string;
}

/**
 * Radio - FauxDS (recreated from Figma MCP)
 *
 * Radios allow users to select only one option among a set of options.
 * Options are typically supported by text labels.
 *
 * Usage tips:
 * - Provide a clear label for each option
 * - Pre-select a default option when possible
 * - Each option must be mutually exclusive
 * - If possible, order options in a way that makes logical sense
 *
 * This implementation was created by extracting design specs directly from Figma.
 */
const Radio = forwardRef<HTMLInputElement, RadioProps>(
  (
    {
      label,
      checked = false,
      disabled = false,
      onChange,
      name,
      value,
      className = "",
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = `radio-figma-${generatedId}`;

    // Determine colors based on state
    const getStateStyles = () => {
      if (disabled) {
        return {
          border: "border-[var(--slate-color-action-border-disabled)]", // #dfe0e1
          bg: "bg-transparent",
          dot: "bg-[var(--slate-color-action-surface-disabled)]", // #f7f7f7
          label: "text-[var(--slate-color-action-text-disabled)]", // #757575
          cursor: "cursor-not-allowed",
        };
      }
      if (checked) {
        return {
          border: "border-[var(--slate-color-action-border-active)]", // #000000
          bg: "bg-transparent",
          dot: "bg-[var(--slate-color-action-surface-active)]", // #000000
          label: "text-[var(--slate-color-action-text-default)]", // #333333
          cursor: "cursor-pointer",
        };
      }
      return {
        border: "border-[var(--slate-color-action-border-default)]", // #333333
        bg: "bg-transparent",
        dot: "bg-transparent",
        label: "text-[var(--slate-color-action-text-default)]", // #333333
        cursor: "cursor-pointer",
      };
    };

    const stateStyles = getStateStyles();

    return (
      <div className={cn("flex items-center", className)}>
        {/* Radio circle */}
        <div
          className={cn(
            "relative flex h-[16px] w-[16px] min-w-[16px] items-center justify-center rounded-full border",
            stateStyles.border,
            stateStyles.bg,
            stateStyles.cursor
          )}
          style={{
            borderRadius: "var(--slate-radius-component-round)",
          }}
        >
          {/* Hidden input */}
          <input
            ref={ref}
            type="radio"
            id={inputId}
            name={name}
            value={value}
            checked={checked}
            onChange={onChange}
            disabled={disabled}
            aria-checked={checked}
            className={cn(
              "absolute h-full w-full appearance-none rounded-full",
              stateStyles.cursor,
              "focus:outline-hidden focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--slate-color-action-border-focus-ring)]"
            )}
            style={{
              borderRadius: "var(--slate-radius-component-round)",
            }}
          />
          {/* Inner dot (when checked) */}
          {checked && (
            <div
              className={cn("h-[8px] w-[8px] rounded-full", stateStyles.dot)}
              style={{
                borderRadius: "var(--slate-radius-component-round)",
              }}
            />
          )}
        </div>

        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "pl-[8px] select-none",
              stateStyles.label,
              stateStyles.cursor
            )}
            style={{
              fontFamily: "var(--slate-typography-family-sans-serif)",
              fontSize: "var(--slate-typography-font-size-200)",
              fontWeight: "var(--slate-typography-weight-regular)",
              lineHeight: "var(--slate-typography-line-height-200)",
              letterSpacing: "var(--slate-typography-letter-spacing-200)",
            }}
          >
            {label}
          </label>
        )}
      </div>
    );
  }
);

Radio.displayName = "Radio";

export default Radio;
